import React from "react";
import { Link } from "react-router-dom";
import { Card } from "./ui/Card";
import type { Lab } from "../services/labService";
import { normalizeLabType } from "../types/lab";

interface LabCardProps {
  lab: Lab;
  onJoin?: (labId: string) => void;
  onUnjoin?: (labId: string) => void;
  onRemove?: ((labId: string) => void) | null;
  onTransferChampion?: (() => void) | null;
  onViewMembers?: (() => void) | null;
  isAdmin?: boolean;
  isLoading?: boolean;
  showDetails?: boolean;
}

export const LabCard: React.FC<LabCardProps> = ({
  lab,
  onJoin,
  onUnjoin,
  onRemove,
  onTransferChampion,
  onViewMembers,
  isAdmin = false,
  isLoading = false,
  showDetails = false,
}) => {
  const isCurrentUserMember: boolean = lab.is_member || false;
  // Set is Champion to false until feature is complete
  const isChampion: boolean = false;
  const isFull: boolean = lab.member_count >= lab.total_fte;
  const labTypeLabel = normalizeLabType(lab.type);

  return (
    <Card variant="secondary">
      <div className="card-body">
        <div className="flex justify-between items-start mb-2">
          <h3 className="card-title text-lg">
            {showDetails && isCurrentUserMember ? (
              <Link to={`/lab/${lab.id}`} className="hover:text-primary">
                {lab.name}
              </Link>
            ) : (
              lab.name
            )}
          </h3>
          <div className="flex gap-2">
            {lab.status === "awaiting_audit" && (
              <div className="badge badge-info badge-sm">Awaiting Audit</div>
            )}
            {isChampion && (
              <div className="badge badge-primary badge-sm">
                LAB ZER0 Champion
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-base-content/70">Type:</span>
            <span className="capitalize">{labTypeLabel}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-base-content/70">Members:</span>
            <span>
              {lab.member_count}/{lab.total_fte}
            </span>
          </div>
          {lab.champion_email && (
            <div className="flex justify-between text-sm">
              <span className="text-base-content/70">Champion:</span>
              <span className="truncate ml-2">{lab.champion_email}</span>
            </div>
          )}
        </div>

        <div className="card-actions justify-end gap-2">
          {/* View Details Button */}
          {showDetails && isCurrentUserMember && (
            <Link to={`/lab/${lab.id}`} className="btn btn-primary btn-sm">
              Go to Lab
            </Link>
          )}

          {/* Join button - shown if not a member and lab is not full */}
          {onJoin && !isCurrentUserMember && !isFull && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => onJoin(lab.id)}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="loading loading-spinner loading-xs"></span>
              ) : (
                "Join"
              )}
            </button>
          )}

          {/* Full lab indicator */}
          {onJoin && !isCurrentUserMember && isFull && (
            <button className="btn btn-disabled btn-sm" disabled>
              Full
            </button>
          )}

          {/* Champion transfer button - shown if user is champion */}
          {onTransferChampion && isChampion && (
            <button
              className="btn btn-warning btn-sm"
              onClick={onTransferChampion}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="loading loading-spinner loading-xs"></span>
              ) : (
                "Transfer Champion"
              )}
            </button>
          )}

          {/* Admin remove button */}
          {onRemove && isAdmin && (
            <button
              className="btn btn-error btn-sm"
              onClick={() => onRemove(lab.id)}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="loading loading-spinner loading-xs"></span>
              ) : (
                "Remove"
              )}
            </button>
          )}

          {/* Leave button - shown if user is a member; disabled if champion */}
          {onUnjoin && isCurrentUserMember && (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => {
                if (!isChampion) {
                  onUnjoin(lab.id);
                }
              }}
              disabled={isLoading || isChampion}
              title={
                isChampion
                  ? "You are the Lab Champion. Transfer the champion role to another member before leaving this lab."
                  : undefined
              }
            >
              {isLoading ? (
                <span className="loading loading-spinner loading-xs"></span>
              ) : (
                "Leave"
              )}
            </button>
          )}

          {/* Admin view members button */}
          {onViewMembers && isAdmin && (
            <button
              className="btn btn-outline btn-sm"
              onClick={onViewMembers}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="loading loading-spinner loading-xs"></span>
              ) : (
                "View Members"
              )}
            </button>
          )}

        </div>
      </div>
    </Card>
  );
};
